import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import { localizeChallenge } from './challengeLocalization';
import {
  formatConstraintLabel,
  formatReviewQueueLabel,
  formatSubmissionStatus,
} from './challengeUtils';
import ChallengePublicLayout from './ChallengePublicLayout';
import './challenges.css';

function formatWorkspaceDate(value, language) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

export default function ChallengeWorkspacePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const workspace = useQuery(api.challenges.getWorkspace);
  const toggleSavedChallenge = useMutation(api.challenges.toggleSavedChallenge);
  const startProjectFromChallenge = useMutation(api.challenges.startProjectFromChallenge);
  const [message, setMessage] = useState('');
  const [busyKey, setBusyKey] = useState('');

  if (workspace === undefined) {
    return (
      <ChallengePublicLayout>
        <section className="ch-route-page">
          <div className="ch-container">
            <div className="ch-empty-state">
              <h3>{t('challengesRuntime.workspace.loadingTitle', { defaultValue: 'Loading your challenge desk…' })}</h3>
              <p>{t('challengesRuntime.workspace.loadingSubtitle', { defaultValue: 'Gathering your saved prompts, submissions, and project links.' })}</p>
            </div>
          </div>
        </section>
      </ChallengePublicLayout>
    );
  }

  if (workspace === null) {
    return <Navigate to="/projects?mode=signin" replace />;
  }

  const handleToggleSave = async (challenge) => {
    setMessage('');
    setBusyKey(`save:${challenge._id}`);
    try {
      await toggleSavedChallenge({ challengeId: challenge._id });
    } catch (error) {
      setMessage(
        error?.data?.message
          || error?.message
          || t('challengesRuntime.detail.saveFailed', {
            defaultValue: 'Could not update the saved state for this challenge.',
          }),
      );
    } finally {
      setBusyKey('');
    }
  };

  const handleStartProject = async (challenge) => {
    setMessage('');
    setBusyKey(`project:${challenge._id}`);
    try {
      const result = await startProjectFromChallenge({ challengeId: challenge._id });
      navigate(`/projects/${result.projectId}/editor?path=${encodeURIComponent(result.entryPath)}`);
    } catch (error) {
      setMessage(
        error?.data?.message
          || error?.message
          || t('challengesRuntime.workspace.projectLaunchFailed', {
            defaultValue: 'Could not open this challenge as a project.',
          }),
      );
    } finally {
      setBusyKey('');
    }
  };

  return (
    <ChallengePublicLayout>
      <section className="ch-route-page">
        <div className="ch-container ch-workspace">
          <div className="ch-breadcrumbs">
            <Link to="/projects">{t('projects.projectHome', { defaultValue: 'Projects' })}</Link>
            <span>/</span>
            <Link to="/challenges">{t('landing.nav.challenges', { defaultValue: 'Challenges' })}</Link>
            <span>/</span>
            <span>{t('challengesRuntime.workspace.breadcrumb', { defaultValue: 'My challenge desk' })}</span>
          </div>

          <div className="ch-detail-hero ch-workspace-hero">
            <div className="ch-detail-main">
              <div className="ch-detail-eyebrow">
                <span>{t('challengesRuntime.workspace.eyebrow', { defaultValue: 'Signed-in challenge workflow' })}</span>
              </div>
              <h1>{t('challengesRuntime.workspace.title', { defaultValue: 'My challenge desk' })}</h1>
              <p>{t('challengesRuntime.workspace.subtitle', { defaultValue: 'Keep your saved prompts, live submissions, and longer project spin-outs in one place.' })}</p>
              <div className="ch-detail-actions">
                <Link to="/challenges" className="ch-btn-primary">
                  {t('challengesRuntime.workspace.discoverAction', { defaultValue: 'Browse more challenges' })}
                </Link>
                <Link to="/projects/rewards" className="ch-btn-secondary">
                  {t('challengesRuntime.workspace.rewardsAction', { defaultValue: 'Open rewards' })}
                </Link>
              </div>
            </div>

            <aside className="ch-workspace-summary">
              <div className="ch-detail-card">
                <span className="ch-section-label">{t('challengesRuntime.workspace.summary.saved', { defaultValue: 'Saved prompts' })}</span>
                <strong>{workspace.summary.savedCount}</strong>
              </div>
              <div className="ch-detail-card">
                <span className="ch-section-label">{t('challengesRuntime.workspace.summary.drafts', { defaultValue: 'Open drafts' })}</span>
                <strong>{workspace.summary.draftCount}</strong>
              </div>
              <div className="ch-detail-card">
                <span className="ch-section-label">{t('challengesRuntime.workspace.summary.inReview', { defaultValue: 'In review' })}</span>
                <strong>{workspace.summary.inReviewCount}</strong>
              </div>
              <div className="ch-detail-card">
                <span className="ch-section-label">{t('challengesRuntime.workspace.summary.approved', { defaultValue: 'Approved entries' })}</span>
                <strong>{workspace.summary.approvedCount}</strong>
              </div>
            </aside>
          </div>

          {message ? <div className="ch-lock-banner">{message}</div> : null}

          <div className="ch-workspace-section">
            <div className="ch-workspace-section-head">
              <div>
                <span className="ch-section-label">{t('challengesRuntime.workspace.sections.submissions.kicker', { defaultValue: 'Current workflow' })}</span>
                <h2>{t('challengesRuntime.workspace.sections.submissions.title', { defaultValue: 'Your challenge submissions' })}</h2>
                <p>{t('challengesRuntime.workspace.sections.submissions.subtitle', { defaultValue: 'Continue drafts, check review status, or push promising entries into a full project.' })}</p>
              </div>
            </div>

            {workspace.submissions.length === 0 ? (
              <div className="ch-empty-state">
                <h3>{t('challengesRuntime.workspace.emptySubmissionsTitle', { defaultValue: 'No challenge submissions yet' })}</h3>
                <p>{t('challengesRuntime.workspace.emptySubmissionsSubtitle', { defaultValue: 'Pick a prompt, start a draft, and it will show up here as soon as you begin.' })}</p>
              </div>
            ) : (
              <div className="ch-workspace-grid">
                {workspace.submissions.map((item) => {
                  const challenge = localizeChallenge(item.challenge, t);
                  const updatedLabel = formatWorkspaceDate(item.submittedAt ?? item.updatedAt, i18n.language);
                  const isDraftState = item.status === 'draft' || item.status === 'rejected';
                  const primaryHref = isDraftState
                    ? `/challenges/${challenge.slug}/write`
                    : `/challenges/${challenge.slug}/submission`;
                  const primaryLabel = isDraftState
                    ? t('challengesRuntime.workspace.openDraftToSubmit', { defaultValue: 'Open draft to submit' })
                    : t('challengesRuntime.workspace.viewSubmission', { defaultValue: 'View submission' });
                  const nextStepCopy = isDraftState
                    ? t('challengesRuntime.workspace.nextStepDraft', {
                        defaultValue: 'Open challenge-submission.md, finish the official entry in the project, then submit it from the challenge page.',
                      })
                    : item.status === 'approved'
                      ? t('challengesRuntime.workspace.nextStepApproved', {
                          defaultValue: 'This entry is already approved. Open the submission page to review it or keep expanding it in the linked project.',
                        })
                      : t('challengesRuntime.workspace.nextStepReview', {
                          defaultValue: 'This entry is already submitted. Open the submission page to track review status or move it back to draft.',
                        });

                  return (
                    <article key={item.submissionId} className="ch-workspace-card">
                      <div className="ch-workspace-card-top">
                        <div className="ch-detail-eyebrow">
                          <span>{challenge.emoji}</span>
                          <span>{challenge.genre}</span>
                          <span>•</span>
                          <span>{formatReviewQueueLabel(item.reviewQueue, t)}</span>
                        </div>
                        <span className="ch-card-badge">
                          {formatSubmissionStatus(item.status, t)}
                        </span>
                      </div>
                      <h3>{challenge.title}</h3>
                      <p>{challenge.description}</p>
                      <div className="ch-workspace-meta">
                        <span>{formatConstraintLabel(challenge, t)}</span>
                        <span>{item.wordCount.toLocaleString()} {t('common.words', { defaultValue: 'words' })}</span>
                        {updatedLabel ? <span>{updatedLabel}</span> : null}
                      </div>
                      <div className="ch-workspace-next-step">
                        <span className="ch-section-label">
                          {t('challengesRuntime.workspace.nextStepLabel', { defaultValue: 'Next step' })}
                        </span>
                        <p>{nextStepCopy}</p>
                      </div>
                      {item.projectTitle ? (
                        <div className="ch-workspace-project-pill">
                          {t('challengesRuntime.workspace.linkedProject', {
                            defaultValue: 'Linked project: {{title}}',
                            title: item.projectTitle,
                          })}
                        </div>
                      ) : null}
                      <div className="ch-workspace-actions">
                        <Link to={primaryHref} className="ch-btn-primary">
                          {primaryLabel}
                        </Link>
                        <Link to={`/challenges/${challenge.slug}`} className="ch-btn-secondary">
                          {t('challengesRuntime.workspace.manageChallenge', { defaultValue: 'Challenge details' })}
                        </Link>
                        {item.projectId ? (
                          <>
                            <Link
                              to={`/projects/${item.projectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_SUBMISSION_PATH)}`}
                              className="ch-btn-secondary"
                            >
                              {t('challengesRuntime.write.openSubmissionFile', { defaultValue: 'Open challenge-submission.md' })}
                            </Link>
                            <Link
                              to={`/projects/${item.projectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_DRAFT_PATH)}`}
                              className="ch-btn-secondary"
                            >
                              {t('challengesRuntime.write.openScratchDraft', { defaultValue: 'Open scratch draft' })}
                            </Link>
                            <Link to={`/projects/${item.projectId}`} className="ch-btn-secondary">
                              {t('challengesRuntime.actions.openProjectHome', { defaultValue: 'Open project home' })}
                            </Link>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="ch-btn-secondary"
                            onClick={() => handleStartProject(challenge)}
                            disabled={busyKey === `project:${challenge._id}`}
                          >
                            {busyKey === `project:${challenge._id}`
                              ? t('challengesRuntime.workspace.openingProject', { defaultValue: 'Opening project…' })
                              : t('challengesRuntime.actions.startAsProject', { defaultValue: 'Start as project' })}
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div className="ch-workspace-section">
            <div className="ch-workspace-section-head">
              <div>
                <span className="ch-section-label">{t('challengesRuntime.workspace.sections.saved.kicker', { defaultValue: 'Return later' })}</span>
                <h2>{t('challengesRuntime.workspace.sections.saved.title', { defaultValue: 'Saved prompts' })}</h2>
                <p>{t('challengesRuntime.workspace.sections.saved.subtitle', { defaultValue: 'Keep challenge ideas on deck until you are ready to draft, brainstorm, or turn them into a project.' })}</p>
              </div>
            </div>

            {workspace.savedChallenges.length === 0 ? (
              <div className="ch-empty-state">
                <h3>{t('challengesRuntime.workspace.emptySavedTitle', { defaultValue: 'No saved prompts yet' })}</h3>
                <p>{t('challengesRuntime.workspace.emptySavedSubtitle', { defaultValue: 'Star prompts on the challenges page and they will show up here with one-click actions.' })}</p>
              </div>
            ) : (
              <div className="ch-workspace-grid">
                {workspace.savedChallenges.map((rawChallenge) => {
                  const challenge = localizeChallenge(rawChallenge, t);
                  return (
                    <article key={challenge._id} className="ch-workspace-card">
                      <div className="ch-workspace-card-top">
                        <div className="ch-detail-eyebrow">
                          <span>{challenge.emoji}</span>
                          <span>{challenge.genre}</span>
                        </div>
                        <span className="ch-card-badge">{formatReviewQueueLabel(challenge.reviewQueue, t)}</span>
                      </div>
                      <h3>{challenge.title}</h3>
                      <p>{challenge.description}</p>
                      <div className="ch-workspace-meta">
                        <span>{formatConstraintLabel(challenge, t)}</span>
                        <span>{challenge.isLocked ? t('challengesRuntime.labels.locked', { defaultValue: 'Locked' }) : t('challengesRuntime.workspace.ready', { defaultValue: 'Ready' })}</span>
                      </div>
                      <div className="ch-workspace-actions">
                        <Link to={`/challenges/${challenge.slug}`} className="ch-btn-primary">
                          {t('challengesRuntime.actions.viewChallenge', { defaultValue: 'View challenge' })}
                        </Link>
                        {!challenge.isLocked ? (
                          <>
                            <Link to={`/challenges/${challenge.slug}/write`} className="ch-btn-secondary">
                              {challenge.hasOpenSubmission
                                ? t('challengesRuntime.actions.continueDraft', { defaultValue: 'Continue draft' })
                                : t('challengesRuntime.actions.startDraft', { defaultValue: 'Start draft' })}
                            </Link>
                            <button
                              type="button"
                              className="ch-btn-secondary"
                              onClick={() => handleStartProject(challenge)}
                              disabled={busyKey === `project:${challenge._id}`}
                            >
                              {busyKey === `project:${challenge._id}`
                                ? t('challengesRuntime.workspace.openingProject', { defaultValue: 'Opening project…' })
                                : t('challengesRuntime.actions.startAsProject', { defaultValue: 'Start as project' })}
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          className="ch-btn-secondary"
                          onClick={() => handleToggleSave(challenge)}
                          disabled={busyKey === `save:${challenge._id}`}
                        >
                          {busyKey === `save:${challenge._id}`
                            ? t('challengesRuntime.actions.savingChallenge', { defaultValue: 'Saving…' })
                            : t('challengesRuntime.actions.unsaveChallenge', { defaultValue: 'Unsave challenge' })}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </ChallengePublicLayout>
  );
}
